import React, { useEffect, useState } from 'react';
import { Bug, ImagePlus, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../design/Button';
import { Modal } from '../design/Modal';
import { apiUrl, getToken } from '../../utils/api';

interface BugReportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/** Fehler melden – Screenshot per Klick oder einfach mit Strg+V einfügen. */
export const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
    const [description, setDescription] = useState('');
    const [screenshot, setScreenshot] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!screenshot) { setPreviewUrl(null); return; }
        const url = URL.createObjectURL(screenshot);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [screenshot]);

    const onPaste = (e: React.ClipboardEvent) => {
        const file = Array.from(e.clipboardData.files).find((f) => f.type.startsWith('image/'));
        if (file) {
            e.preventDefault();
            setScreenshot(file);
            toast.success('Screenshot eingefügt');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!description.trim()) {
            toast.error('Bitte kurz beschreiben, was passiert ist.');
            return;
        }
        setLoading(true);
        const formData = new FormData();
        formData.append('description', description);
        if (screenshot) formData.append('screenshot', screenshot);
        try {
            const res = await fetch(apiUrl('/api/bugs'), {
                method: 'POST',
                headers: { Authorization: `Bearer ${getToken()}` },
                body: formData,
            });
            const data = await res.json().catch(() => ({}));
            if (data.success) {
                toast.success('Danke! Fehler gemeldet.');
                setDescription('');
                setScreenshot(null);
                onClose();
            } else {
                toast.error('Senden fehlgeschlagen');
            }
        } catch {
            toast.error('Keine Verbindung zum Panel');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Fehler melden" size="md">
            <form onSubmit={handleSubmit} onPaste={onPaste} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-fg mb-2">Was ist passiert?</label>
                    <textarea
                        autoFocus
                        className="w-full min-h-[130px] resize-y rounded-xl bg-black/25 border border-line p-3 text-sm text-fg placeholder:text-fg-muted/70 outline-none focus:border-ember/70 focus:ring-2 focus:ring-ember/20 transition-all"
                        placeholder="Auf welchem Server, was hast du gemacht, was ist passiert – und was hättest du erwartet?"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-fg mb-2">
                        Screenshot <span className="font-normal text-fg-muted">(optional – auch mit Strg+V einfügen)</span>
                    </label>
                    {previewUrl ? (
                        <div className="relative rounded-xl overflow-hidden border border-line">
                            <img src={previewUrl} alt="Ausgewählter Screenshot" className="w-full max-h-56 object-contain bg-black/40" />
                            <button
                                type="button"
                                onClick={() => setScreenshot(null)}
                                aria-label="Screenshot entfernen"
                                className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/70 text-fg hover:text-red-400"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ) : (
                        <label className="flex flex-col items-center gap-2 w-full rounded-xl border-2 border-dashed border-line-strong p-6 text-center cursor-pointer text-fg-muted hover:text-fg hover:border-white/20 hover:bg-white/[0.03] transition-all">
                            <input type="file" className="hidden" accept="image/*" onChange={(e) => setScreenshot(e.target.files?.[0] || null)} />
                            <ImagePlus size={22} />
                            <span className="text-xs">Klicken, um ein Bild auszuwählen</span>
                        </label>
                    )}
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-line">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>Abbrechen</Button>
                    <Button type="submit" disabled={loading} className="min-w-[120px]">
                        {loading ? <Loader2 className="animate-spin" size={16} /> : <Bug size={16} />}
                        {loading ? 'Sende …' : 'Senden'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
